// Angular
import { Injectable } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

// Data Contracts
import { ClerkReviewFilterSettings } from '@data-contracts';

// Local
import { ALL_COURTS_VALUE } from '@feature/crl/clerk-review-filter/court-filter-control/court-filter-control.component';
import { DEFAULT_DOCUMENTS_VALUE } from '@feature/crl/clerk-review-filter/document-filter-control/document-filter-control.component';
import { ALL_JUDGES_VALUE } from '@feature/crl/clerk-review-filter/judge-filter-control/judge-filter-control.component';
import { ALL_QUEUES_VALUE } from '@feature/crl/clerk-review-filter/queue-filter-control/queue-filter-control.component';

/**
 * An interface to define the structure of the filter form.
 */
export interface ClerkReviewFilterForm {
  /**
   * The FormControl for the "Court" dropdown.
   */ 
  courtFC: FormControl<number>;

  /**
   * The FormControl for the "Queue" dropdown.
   */
  queueFC: FormControl<string>;

  /**
   * The FormControl for the "Judge" dropdown.
   */
  judgeFC: FormControl<number>;

  /**
   * The FormControl for the "Documents" dropdown.
   */
  documentsFC: FormControl<boolean>;

  /**
   * The FormGroup for the "Date" date range picker.
   */
  dateRangeFG: FormGroup<{
    start: FormControl<Date | null>;
    end: FormControl<Date | null>;
  }>
}

/**
 * A service to create and store the filter form.
 */
@Injectable({ providedIn: 'root' })
export class ClerkReviewFilterFormService {
  /**
   * The filter form.
   */
  private _filterForm: FormGroup<ClerkReviewFilterForm>;

  /**
   * A getter to return the filter form.
   */
  get filterForm(): FormGroup<ClerkReviewFilterForm> {
    return this._filterForm;
  }

  /**
   * A getter to return the court form control.
   */
  get courtFormControl(): FormControl<number> {
    return this._filterForm.controls.courtFC;
  }
  
  /**
   * A getter to return the queue form control.
   */
  get queueFormControl(): FormControl<string> {
    return this._filterForm.controls.queueFC;
  }

  /**
   * A getter to return the judge form control.
   */
  get judgeFormControl(): FormControl<number> {
    return this._filterForm.controls.judgeFC;
  }

  /**
   * A getter to return the documents form control.
   */
  get documentsFormControl(): FormControl<boolean> {
    return this._filterForm.controls.documentsFC;
  }

  /**
   * A public function to create the filter form with default values.
   */
  createFilterForm(clerkReviewFilterSettings: ClerkReviewFilterSettings): void {
    // Derive the default selected values for the filter form.
    const courtValue = clerkReviewFilterSettings.courts.length === 1 ? clerkReviewFilterSettings.courts[0].courtId : ALL_COURTS_VALUE;
    const queueValue = clerkReviewFilterSettings.queues.length === 1 ? clerkReviewFilterSettings.queues[0].clerkReviewQueueId : ALL_QUEUES_VALUE;
    const judgeValue = clerkReviewFilterSettings.judges.length === 1 ? clerkReviewFilterSettings.judges[0].judgeId : ALL_JUDGES_VALUE;

    // Create the filter form.with the default selected values.
    this._filterForm = new FormGroup<ClerkReviewFilterForm>({
      courtFC: new FormControl<number>(courtValue),
      queueFC: new FormControl<string>(queueValue),
      judgeFC: new FormControl<number>(judgeValue),
      documentsFC: new FormControl<boolean>(DEFAULT_DOCUMENTS_VALUE),
      dateRangeFG: new FormGroup({
        start: new FormControl<Date | null>(null),
        end: new FormControl<Date | null>(null)
      })
    });
  }
}
